import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, effect, inject, Input } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatPaginatorModule, PageEvent } from '@angular/material/paginator';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSortModule, Sort } from '@angular/material/sort';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { MatTooltipModule } from '@angular/material/tooltip';
import { RouterLink } from '@angular/router';

import { Store } from '@ngrx/store';

import { OrdersActions } from '../../../../@store/orders/orders.actions';
import {
  selectSingleList,
  selectSingleListError,
  selectSingleListLoading,
  selectSingleListResults,
} from '../../../../@store/orders/orders.selectors';
import { selectIsOrdersManager } from '../../../../@store/user/user.selectors';
import { CONFIG } from '../../../../config';
import { PaginationComponent } from '../../../@shared/pagination/pagination.component';

@Component({
  selector: 'ciwc-saved-list-table',
  standalone: true,
  imports: [
    CommonModule,
    MatTableModule,
    MatButtonModule,
    MatPaginatorModule,
    MatSortModule,
    MatIconModule,
    MatDialogModule,
    MatCardModule,
    PaginationComponent,
    MatCheckboxModule,
    MatIconModule,
    MatProgressSpinnerModule,
    MatFormFieldModule,
    MatInputModule,
    MatTooltipModule,
    RouterLink,
  ],
  templateUrl: './saved-list-table.component.html',
  styleUrls: ['./saved-list-table.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SavedListTableComponent {
  @Input() listId!: number | undefined;

  private readonly store = inject(Store);
  private readonly dialog = inject(MatDialog);

  readonly singleList = this.store.selectSignal(selectSingleList);
  readonly singleListResults = this.store.selectSignal(selectSingleListResults);
  readonly singleListLoading = this.store.selectSignal(selectSingleListLoading);
  readonly singleListError = this.store.selectSignal(selectSingleListError);
  readonly isOrdersManager = this.store.selectSignal(selectIsOrdersManager);
  readonly CONFIG = CONFIG;

  protected readonly Math = Math;

  dataSource: MatTableDataSource<unknown> = new MatTableDataSource<unknown>();
  displayedColumns: string[] = [
    'orderId',
    'description',
    'clientPurchaseOrder',
    'caNumber',
    'promisedDate',
    'dateCreated',
    'status',
    'client',
    'assignee',
    'totalQuantity',
    'orderTotalPrice',
    'orderTotalPriceNotShipped',
    'dateUpdated',
    'actions',
  ];
  pageSizeOptions = CONFIG.PAGE_SIZE_OPTIONS;
  pageSize = CONFIG.PAGE_SIZE;
  pageIndex = 0;
  errorText = CONFIG.ERROR_MESSAGES.DEFAULT;
  currentSort: Sort = { active: 'dateCreated', direction: 'desc' };

  constructor() {
    effect(() => {
      const list = this.singleListResults();
      if (list) {
        const ordersTableData = list.orders.map((row: unknown) => ({
          ...(row as Record<string, unknown>),
          isPastDue: this.isPastDue((row as { dueDate: string }).dueDate),
        }));
        this.dataSource.data = ordersTableData;
      }
    });
  }

  /**
   * Checks if the given due date is past due.
   * @param dueDate
   * @returns {boolean}
   */
  isPastDue(dueDate: string): boolean {
    if (!dueDate) return false;
    // Parse as local or UTC, fallback to Date constructor
    const due = new Date(dueDate.replace(' ', 'T'));
    return due.getTime() < Date.now();
  }

  sortData(sort: Sort): void {
    this.currentSort = sort;

    const formattedSort =
      this.currentSort.direction === 'desc'
        ? `-${this.currentSort.active}`
        : this.currentSort.active;

    if (this.listId) {
      this.store.dispatch(
        OrdersActions.loadSingleList({
          id: this.listId,
          payload: { offset: this.pageIndex + 1, limit: this.pageSize, sort: formattedSort },
        }),
      );
    }
  }

  handlePageEvent(event: PageEvent): void {
    this.pageSize = event.pageSize;
    this.pageIndex = event.pageIndex;

    const formattedSort =
      this.currentSort.direction === 'desc'
        ? `-${this.currentSort.active}`
        : this.currentSort.active;

    if (this.listId) {
      this.store.dispatch(
        OrdersActions.loadSingleList({
          id: this.listId,
          payload: {
            offset: this.pageIndex + 1,
            limit: this.pageSize,
            sort: formattedSort,
          },
        }),
      );
    }
  }
}
